import { HttpClient } from '@angular/common/http';
import { UserInfo } from '@core/models/interfaces/auth';
import { AuthService } from '@core/services/auth.service';
import { Store } from '@ngrx/store';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPermissionsService } from 'ngx-permissions';
import { PrimeNGConfig } from 'primeng/api';
import { delayWhen, lastValueFrom, retryWhen, timer } from 'rxjs';

import { flattenPermissionCodes } from '@shared/utils/flattenPermissionCodes.util';
import { AppState } from '@state/app.state';
import { logout, setAuthState, setFacebookAccounts } from '@state/auth/auth.actions';
import { FacebookAdsAdminService } from '@core/services/facebook.service.ts/facebook-ads-admin.service';
import { setFacebookConfig } from '@state/system/system.actions';

export const initialFactory = (store: Store<AppState>, authService: AuthService, primeConfig: PrimeNGConfig) => {
  primeConfig.ripple = true;
  const accessToken = authService.getLocalToken();
  let localUser: UserInfo | undefined;
  try {
    localUser = JSON.parse(authService.getLocalUser() as string);
  } catch (error) {
    localUser = undefined;
  }

  if (!localUser || !accessToken) {
    return () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      store.dispatch(setAuthState({ userInfo: undefined, isLoggedIn: false, accessToken: '' }));
    };
  } else {
    return () => {
      store.dispatch(setAuthState({ userInfo: localUser, isLoggedIn: true, accessToken }));
    };
  }
};

export function permissionFactory(store: Store<AppState>, authService: AuthService, ps: NgxPermissionsService) {
  return async () => {
    if (authService.getLocalToken()) {
      return lastValueFrom(
        authService.getMyPermission().pipe(retryWhen(errors => errors.pipe(delayWhen(() => timer(5000)))))
      )
        .then(res => {
          if (res.data) {
            const permissions = flattenPermissionCodes(res.data);
            ps.loadPermissions(permissions);
          }
        })
        .catch(() => {
          const token = authService.getLocalToken();
          if (token) {
            store.dispatch(logout({ token }));
          }
        });
    }
  };
}

export function facebookAccountFactory(
  store: Store<AppState>,
  authService: AuthService,
  facebookAdsAdminService: FacebookAdsAdminService
) {
  return async () => {
    if (authService.getLocalToken()) {
      return lastValueFrom(facebookAdsAdminService.getFacebookAccounts())
        .then(res => {
          if (res.data?.length > 0) {
            store.dispatch(setFacebookAccounts({ facebookAccounts: res.data }));
          }
        })
        .catch(() => {});
    }
  };
}

export function facebookAppConfig(
  store: Store<AppState>,
  authService: AuthService,
  facebookAdsAdminService: FacebookAdsAdminService
) {
  return async () => {
    if (authService.getLocalToken()) {
      return lastValueFrom(facebookAdsAdminService.getAppConfig())
        .then(res => {
          if (res.data) {
            store.dispatch(setFacebookConfig({ appId: res.data['app-id'], configId: res.data['app-config-id'] }));
          }
        })
        .catch(() => {});
    }
  };
}

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}
